import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  timeout: 90000,
  withCredentials: true,
});

// ---------- Token storage (works even in 3rd-party iframe contexts where cookies are blocked) ----------
const ACCESS_KEY = "atelier.access_token";
const REFRESH_KEY = "atelier.refresh_token";

export function setAuthTokens({ access_token, refresh_token }) {
  try {
    if (access_token) localStorage.setItem(ACCESS_KEY, access_token);
    if (refresh_token) localStorage.setItem(REFRESH_KEY, refresh_token);
  } catch {
    /* localStorage may be unavailable in some embeds */
  }
}

export function getAccessToken() {
  try {
    return localStorage.getItem(ACCESS_KEY);
  } catch {
    return null;
  }
}

export function getRefreshToken() {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

export function clearAuthTokens() {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* noop */
  }
}

// Attach the Bearer token on every request as a robust fallback to cookies.
api.interceptors.request.use((config) => {
  const tok = getAccessToken();
  if (tok && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${tok}`;
  }
  return config;
});

// Auto-redirect to /login on 401 (except for the /auth/me probe and the auth endpoints themselves)
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err?.config?.url || "";
    const status = err?.response?.status;
    if (
      status === 401 &&
      !url.includes("/auth/login") &&
      !url.includes("/auth/register") &&
      !url.includes("/auth/me") &&
      !url.includes("/auth/refresh") &&
      typeof window !== "undefined" &&
      !window.location.pathname.startsWith("/login") &&
      !window.location.pathname.startsWith("/register") &&
      !window.location.pathname.startsWith("/shared/")
    ) {
      clearAuthTokens();
      const from = window.location.pathname + window.location.search;
      window.location.replace(`/login?from=${encodeURIComponent(from)}`);
    }
    return Promise.reject(err);
  }
);

export default api;

// ---------- Auth ----------
export const authMe = () => api.get("/auth/me").then((r) => r.data);
export const authLogin = (data) =>
  api.post("/auth/login", data).then((r) => r.data);
export const authRegister = (data) =>
  api.post("/auth/register", data).then((r) => r.data);
export const authLogout = () => api.post("/auth/logout").then((r) => r.data);
export const authRefresh = () => api.post("/auth/refresh").then((r) => r.data);

// ---------- Admin ----------
export const adminGetSmtp = () =>
  api.get("/admin/settings/smtp").then((r) => r.data);
export const adminSaveSmtp = (data) =>
  api.put("/admin/settings/smtp", data).then((r) => r.data);
export const adminTestSmtp = (to_email) =>
  api.post("/admin/settings/smtp/test", { to_email }).then((r) => r.data);
export const adminListUsers = () =>
  api.get("/admin/users").then((r) => r.data);
export const adminUpdateUser = (id, data) =>
  api.put(`/admin/users/${id}`, data).then((r) => r.data);
export const adminDeleteUser = (id) =>
  api.delete(`/admin/users/${id}`).then((r) => r.data);

// ---------- Projects ----------
export const fetchProjects = () => api.get("/projects").then((r) => r.data);
export const createProject = (data) => api.post("/projects", data).then((r) => r.data);
export const fetchProject = (id) => api.get(`/projects/${id}`).then((r) => r.data);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data).then((r) => r.data);
export const deleteProject = (id) => api.delete(`/projects/${id}`).then((r) => r.data);

// ---------- Images ----------
export const fetchProjectImages = (id, params = {}) =>
  api.get(`/projects/${id}/images`, { params }).then((r) => r.data);
export const uploadImage = (id, data) =>
  api.post(`/projects/${id}/images`, data).then((r) => r.data);
export const processDocument = (id, data) =>
  api.post(`/projects/${id}/documents/process`, data).then((r) => r.data);
export const updateImage = (id, data) => api.put(`/images/${id}`, data).then((r) => r.data);
export const deleteImage = (id, cascade = false) =>
  api.delete(`/images/${id}`, { params: { cascade } }).then((r) => r.data);
export const getImageUsage = (id) =>
  api.get(`/images/${id}/usage`).then((r) => r.data);

// ---------- Categories ----------
export const renameCategory = (projectId, oldName, newName) =>
  api.put(`/projects/${projectId}/categories/rename`, { old: oldName, new: newName }).then((r) => r.data);
export const deleteCategoryApi = (projectId, name, reassignTo = "inspiration") =>
  api
    .post(`/projects/${projectId}/categories/delete`, { name, reassign_to: reassignTo })
    .then((r) => r.data);

// ---------- Tabs ----------
export const addTab = (projectId, data) =>
  api.post(`/projects/${projectId}/tabs`, data).then((r) => r.data);
export const updateTab = (projectId, tabId, data) =>
  api.put(`/projects/${projectId}/tabs/${tabId}`, data).then((r) => r.data);
export const deleteTab = (projectId, tabId) =>
  api.delete(`/projects/${projectId}/tabs/${tabId}`).then((r) => r.data);
export const reorderTabs = (projectId, ids) =>
  api.put(`/projects/${projectId}/tabs/reorder`, { ids }).then((r) => r.data);

// ---------- Rooms ----------
export const addRoom = (projectId, data) =>
  api.post(`/projects/${projectId}/rooms`, data).then((r) => r.data);
export const updateRoom = (projectId, roomId, data) =>
  api.put(`/projects/${projectId}/rooms/${roomId}`, data).then((r) => r.data);
export const deleteRoom = (projectId, roomId) =>
  api.delete(`/projects/${projectId}/rooms/${roomId}`).then((r) => r.data);
export const autoBuildRoomMoodboard = (projectId, roomId, data) =>
  api
    .post(`/projects/${projectId}/rooms/${roomId}/moodboard/auto-build`, data)
    .then((r) => r.data);

// ---------- Archive ----------
export const archiveProject = (projectId) =>
  api.post(`/projects/${projectId}/archive`).then((r) => r.data);
export const unarchiveProject = (projectId) =>
  api.post(`/projects/${projectId}/unarchive`).then((r) => r.data);
export const fetchProjectsByStatus = (status = "false") =>
  api.get(`/projects`, { params: { archived: status } }).then((r) => r.data);

// ---------- AI ----------
export const analyzeImage = (data) => api.post(`/ai/analyze`, data).then((r) => r.data);

// ---------- URL Import ----------
export const importFromUrl = (data) => api.post(`/import/url`, data).then((r) => r.data);

// ---------- Moodboards ----------
export const fetchMoodboards = (projectId) =>
  api.get(`/projects/${projectId}/moodboards`).then((r) => r.data);
export const fetchMoodboard = (id) => api.get(`/moodboards/${id}`).then((r) => r.data);
export const createMoodboard = (data) => api.post(`/moodboards`, data).then((r) => r.data);
export const updateMoodboard = (id, data) =>
  api.put(`/moodboards/${id}`, data).then((r) => r.data);
export const deleteMoodboard = (id) => api.delete(`/moodboards/${id}`).then((r) => r.data);
export const generateAiMoodboard = (data) =>
  api.post(`/moodboards/ai-generate`, data).then((r) => r.data);

// ---------- Floorplans ----------
export const fetchFloorplans = (projectId) =>
  api.get(`/projects/${projectId}/floorplans`).then((r) => r.data);
export const createFloorplan = (data) => api.post(`/floorplans`, data).then((r) => r.data);
export const updateFloorplan = (id, data) =>
  api.put(`/floorplans/${id}`, data).then((r) => r.data);
export const deleteFloorplan = (id) => api.delete(`/floorplans/${id}`).then((r) => r.data);

// ---------- Products ----------
export const fetchProducts = (projectId, tabId) =>
  api
    .get(`/projects/${projectId}/products`, { params: tabId ? { tab_id: tabId } : {} })
    .then((r) => r.data);
export const createProduct = (data) => api.post(`/products`, data).then((r) => r.data);
export const updateProduct = (id, data) =>
  api.put(`/products/${id}`, data).then((r) => r.data);
export const deleteProduct = (id) => api.delete(`/products/${id}`).then((r) => r.data);

// ---------- Search ----------
export const searchImages = (params) =>
  api.get(`/search`, { params }).then((r) => r.data);

// ---------- Public Share Links ----------
export const createShare = (data) =>
  api.post(`/share`, data).then((r) => r.data);

export const revokeShare = (token) =>
  api.delete(`/share/${token}`).then((r) => r.data);

export const regenerateShare = (token) =>
  api.post(`/share/${token}/regenerate`).then((r) => r.data);

export const lookupShare = (type, target_id) =>
  api.get(`/share/lookup`, { params: { type, target_id } }).then((r) => r.data);

export const fetchPublicShare = (token) =>
  api.get(`/public/share/${token}`).then((r) => r.data);

// ---------- Profile ----------
export const updateProfile = (data) =>
  api.put(`/auth/profile`, data).then((r) => r.data);
export const changePassword = (data) =>
  api.put(`/auth/password`, data).then((r) => r.data);

// ---------- Billing / Subscriptions ----------
export const billingMe = () => api.get(`/billing/me`).then((r) => r.data);
export const billingCancel = () =>
  api.post(`/billing/cancel`).then((r) => r.data);
export const billingReactivate = () =>
  api.post(`/billing/reactivate`).then((r) => r.data);
export const applyVoucher = (code) =>
  api.post(`/billing/apply-voucher`, { code }).then((r) => r.data);

// ---------- Payments (PayFast / PayPal) ----------
export const getPaymentConfig = () =>
  api.get(`/payment-config`).then((r) => r.data);
export const checkoutPayfast = (plan = "monthly", promo_code) =>
  api.post(`/checkout/payfast`, { plan, ...(promo_code ? { promo_code } : {}) }).then((r) => r.data);
export const checkoutPaypal = (plan = "monthly", promo_code) =>
  api.post(`/checkout/paypal`, { plan, ...(promo_code ? { promo_code } : {}) }).then((r) => r.data);
export const capturePaypal = (order_id) =>
  api.post(`/checkout/paypal/capture`, { order_id }).then((r) => r.data);
export const checkoutEft = (plan = "monthly", promo_code) =>
  api.post(`/checkout/eft`, { plan, ...(promo_code ? { promo_code } : {}) }).then((r) => r.data);
export const eftClaim = () =>
  api.post(`/checkout/eft/claim`).then((r) => r.data);
export const eftCancel = () =>
  api.post(`/checkout/eft/cancel`).then((r) => r.data);
export const adminListEftPayments = (status = "pending") =>
  api.get(`/admin/eft-payments`, { params: { status } }).then((r) => r.data);
export const adminConfirmEftPayment = (id) =>
  api.post(`/admin/eft-payments/${id}/confirm`).then((r) => r.data);
export const adminRejectEftPayment = (id) =>
  api.post(`/admin/eft-payments/${id}/reject`).then((r) => r.data);

// ---------- Admin: Payment settings ----------
export const adminGetPaymentSettings = () =>
  api.get(`/admin/payment-settings`).then((r) => r.data);
export const adminSavePaymentSettings = (data) =>
  api.put(`/admin/payment-settings`, data).then((r) => r.data);

// ---------- Admin: Vouchers ----------
export const adminListVouchers = () =>
  api.get(`/admin/vouchers`).then((r) => r.data);
export const adminCreateVoucher = (data) =>
  api.post(`/admin/vouchers`, data).then((r) => r.data);
export const adminDeleteVoucher = (id) =>
  api.delete(`/admin/vouchers/${id}`).then((r) => r.data);

// ---------- Collaboration ----------
export const fetchProjectMembers = (projectId) =>
  api.get(`/projects/${projectId}/members`).then((r) => r.data);
export const inviteCollaborator = (projectId, data) =>
  api.post(`/projects/${projectId}/invite`, data).then((r) => r.data);
export const updateProjectMember = (projectId, userId, data) =>
  api.put(`/projects/${projectId}/members/${userId}`, data).then((r) => r.data);
export const removeProjectMember = (projectId, userId) =>
  api.delete(`/projects/${projectId}/members/${userId}`).then((r) => r.data);
export const revokeProjectInvite = (projectId, inviteId) =>
  api.delete(`/projects/${projectId}/invites/${inviteId}`).then((r) => r.data);
export const fetchPublicInvite = (token) =>
  api.get(`/public/invites/${token}`).then((r) => r.data);
export const acceptInvite = (token) =>
  api.post(`/invites/${token}/accept`).then((r) => r.data);
